import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

// Components
import { Box, Typography, Container, Stack, Tabs, Tab, IconButton, CircularProgress } from "@mui/material";
import FormDialog from "../FormDialog";
import DeleteDialog from "../DeleteDialog";

// Icons
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import GroupIcon from "@mui/icons-material/Group";
import TopicIcon from "@mui/icons-material/Topic";
import DnsIcon from "@mui/icons-material/Dns";
import AssignmentIcon from "@mui/icons-material/Assignment";

// Services
import UserService from "../../../services/UserService";

// Sections
import CompanyDetails from "./CompanyDetails/CompanyDetails";
import AccountDetails from "./AccountDetails/AccountDetails";
import UserLegalDocuments from "./UserLegalDocuments/UserLegalDocuments";
import SubscriptionDetails from "./SubscriptionDetails/SubscriptionDetails";
import BusinessDetails from "./BusinessDetails/BusinessDetails";

const UserProfile = ({ match }) => {
  const history = useHistory();
  const [folder, setFolder] = useState([]);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const userId = match.params.id;

  // Modals state
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const fetchUserData = async () => {
    try {
      const response = await UserService.getFolder(userId);
      setFolder(response.data.folder);
      setUser(response.data.user);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = () => {
    fetchUserData();
  };

  useEffect(() => {
    refresh();
  }, [userId]);

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
      sx: { fontWeight: 700, px: 2, m: 0 },
    };
  }

  function CustomTabPanel(props) {
    const { children, currentTab, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={currentTab !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {currentTab === index && <Box>{children}</Box>}
      </div>
    );
  }

  const handleTabChange = (event, tab) => {
    setCurrentTab(tab);
  };

  return (
    <>
      {isUpdateModalOpen && (
        <FormDialog item={user} handleClose={() => setIsUpdateModalOpen(false)} refresh={refresh} />
      )}

      {isDeleteModalOpen && (
        <DeleteDialog item={user} handleClose={() => setIsDeleteModalOpen(false)} refresh={refresh} />
      )}
      <Box
        component="main"
        sx={{
          alignItems: "center",
          display: "flex",
          marginTop: 3,
          marginBottom: 10,
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={3} alignContent="center" justifyContent="start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton underline="hover" color="inherit" onClick={() => history.push("/users")}>
                <ChevronLeftIcon />
              </IconButton>
              <GroupIcon sx={{ color: "text.secondary" }} />
              <Typography color="text.secondary">/ #{userId}</Typography>
            </Stack>

            {isLoading ? (
              <Stack spacing={2} direction="row" alignItems="center" justifyContent="center" sx={{ marginTop: 3 }}>
                <CircularProgress />
              </Stack>
            ) : (
              <>
                <Stack spacing={2} direction="row">
                  <AccountDetails
                    user={user}
                    openDeleteModal={() => setIsDeleteModalOpen(true)}
                    openUpdateModal={() => setIsUpdateModalOpen(true)}
                  />
                  <CompanyDetails user={user} refresh={refresh} />
                </Stack>

                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs value={currentTab} onChange={handleTabChange} sx={{ gap: 0, mb: 1 }}>
                    <Tab
                      label={
                        <Stack direction="row" alignItems="center" gap={1}>
                          <TopicIcon sx={{ width: 20 }} /> Documents légaux
                        </Stack>
                      }
                      {...a11yProps(0)}
                    />
                    <Tab
                      label={
                        <Stack direction="row" alignItems="center" gap={1}>
                          <DnsIcon sx={{ width: 20 }} /> Modules
                        </Stack>
                      }
                      {...a11yProps(1)}
                    />
                    <Tab
                      label={
                        <Stack direction="row" alignItems="center" gap={1}>
                          <AssignmentIcon sx={{ width: 20 }} /> Entreprise
                        </Stack>
                      }
                      {...a11yProps(2)}
                    />
                  </Tabs>
                  <CustomTabPanel currentTab={currentTab} index={0}>
                    <UserLegalDocuments items={folder} user={user} refreshData={refresh} />
                  </CustomTabPanel>
                  <CustomTabPanel currentTab={currentTab} index={1}>
                    <SubscriptionDetails user={user} />
                  </CustomTabPanel>
                  <CustomTabPanel currentTab={currentTab} index={2}>
                    <BusinessDetails user={user} />
                  </CustomTabPanel>
                </Box>
              </>
            )}
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default UserProfile;
